import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import CreateCampaign from './pages/CreateCampaign';
import EditCampaign from './pages/EditCampaign';
import Proposals from './pages/Proposals';
import ProposalDetail from './pages/ProposalDetail';
import CreateProposal from './pages/CreateProposal';
import Roles from './pages/Roles';
import Tiers from './pages/Tiers';
import Hunters from './pages/Hunters';
import HunterDetail from './pages/HunterDetail';
import Users from './pages/Users';
import Profile from './pages/Profile';
import Payouts from './pages/Payouts';
import Leaderboard from './pages/Leaderboard';
import Settings from './pages/Settings';
import Announcements from './pages/Announcements';
import TestingPayment from './pages/TestingPayment';
import Layout from './components/Layout';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!profile) {
    return <Navigate to="/login" />;
  }

  // Admin selalu bisa akses semua route
  if (profile.role === 'admin') {
    return children;
  }

  if (allowedRoles && !allowedRoles.includes(profile.role)) {
    // Redirect basic role to campaigns page
    if (profile.role === 'basic') {
      return <Navigate to="/campaigns" replace />;
    }
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600">Access Denied</div>
      </div>
    );
  }

  return children;
};

const AppRoutes = () => {
  const { profile, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // Redirect basic and admin_operator role from root to campaigns
  const rootRedirect = (profile?.role === 'basic' || profile?.role === 'admin_operator') ? '/campaigns' : '/';

  return (
    <Routes>
      <Route path="/login" element={!profile ? <Login /> : <Navigate to={rootRedirect} />} />
      <Route
        path="/"
        element={
          <PrivateRoute allowedRoles={['admin', 'management', 'campaign_manager', 'hunter']}>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <PrivateRoute>
            <Layout>
              <Campaigns />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <PrivateRoute>
            <Layout>
              <CampaignDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/create"
        element={
          <PrivateRoute allowedRoles={['campaign_manager', 'admin']}>
            <Layout>
              <CreateCampaign />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/campaigns/:id/edit"
        element={
          <PrivateRoute allowedRoles={['campaign_manager', 'admin']}>
            <Layout>
              <EditCampaign />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/proposals"
        element={
          <PrivateRoute allowedRoles={['admin', 'management', 'campaign_manager', 'hunter']}>
            <Layout>
              <Proposals />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/proposals/:id"
        element={
          <PrivateRoute>
            <Layout>
              <ProposalDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/proposals/create"
        element={
          <PrivateRoute allowedRoles={['hunter', 'basic']}>
            <Layout>
              <CreateProposal />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/roles"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <Roles />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/tiers"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <Tiers />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/hunters"
        element={
          <PrivateRoute allowedRoles={['admin', 'management', 'campaign_manager']}>
            <Layout>
              <Hunters />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/hunters/:userId"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <HunterDetail />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <Users />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <PrivateRoute allowedRoles={['basic', 'hunter', 'admin', 'management', 'campaign_manager', 'admin_operator']}>
            <Layout>
              <Profile />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/payouts"
        element={
          <PrivateRoute allowedRoles={['admin', 'management', 'hunter']}>
            <Layout>
              <Payouts />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <PrivateRoute allowedRoles={['admin', 'management', 'campaign_manager', 'hunter', 'basic']}>
            <Layout>
              <Leaderboard />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <Settings />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/announcements"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <Announcements />
            </Layout>
          </PrivateRoute>
        }
      />
      <Route
        path="/testing-payment"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <Layout>
              <TestingPayment />
            </Layout>
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;

